import React, {
  FC, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  AlarmCallEvent,
  AlarmCallEventAudioPhase,
  AlarmCallEventAudioTranscriptSegment,
  AlarmCallEventChatPhase,
  AlarmCallEventContentType,
  AlarmCallEventTranscriptState,
  AlarmCallParticipant,
  AlarmCallType,
  AlarmLogCallSessionPhase,
  AlarmLogEvent,
  AlarmLogEventKind,
} from '@x-guard/xgac-types/xgac';
import moment from 'moment';
import cx from 'clsx';
import { Icon } from '@mdi/react';
import { mdiPause, mdiPlay, mdiTranslate } from '@mdi/js';
import { useLocale } from 'lib';
import { ISOLang } from 'types';
import { heartbeatMs } from 'features/details/chat';
import { operatorDisplayNames } from 'features/details/chat/formatLog';
import colors from 'style/colors.module.scss';
import messageStyles from './messages/message/style.module.scss';
import styles from './timeline.module.scss';

type OptimisticOperator = {
  _id: string;
  message: string;
  createdAt: string;
  name: string;
};

type Props = {
  callEvents: AlarmCallEvent[];
  participants: AlarmCallParticipant[];
  operatorMessages: AlarmLogEvent[];
  optimisticOperator: OptimisticOperator[];
  logs: AlarmLogEvent[];
};

type Row = {
  id: string;
  at: number;
  node: React.ReactNode;
  ownText?: string;
};

const localeCode = (iso: ISOLang): string => String(iso).slice(0, 2);

const formatDuration = (durationMs: number): string => {

  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;

};

const voiceBarCount = 64;

let currentVoice: HTMLAudioElement | null = null;

const voiceBars = (waveform: number[]): number[] => {

  if (waveform.length === 0) {

    return Array.from({ length: voiceBarCount }, (_, index) => 0.2 + (Math.sin(index) + 1) * 0.15);

  }

  const gated = Array.from({ length: voiceBarCount }, (_, index) => {

    const start = Math.floor((index * waveform.length) / voiceBarCount);
    const end = Math.max(start + 1, Math.floor(((index + 1) * waveform.length) / voiceBarCount));

    return Math.max(0, Math.max(...waveform.slice(start, end)) - 0.01);

  });
  const peak = Math.max(...gated);

  if (peak <= 0) return gated;

  return gated.map((value) => Math.sqrt(value / peak));

};

type Playback = {
  playing: boolean;
  progress: number;
  audioDurationMs?: number;
};

const VoicePlayer: FC<{
  src?: string;
  durationMs?: number;
  waveform: number[];
  playback: Playback;
  onPlayback: (change: Partial<Playback>) => void;
}> = ({
  src, durationMs, waveform, playback: { playing, progress }, onPlayback,
}) => {

  const { t } = useLocale();
  const audioRef = useRef<HTMLAudioElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const setPlaying = (value: boolean) => onPlayback({ playing: value });
  const setProgress = (value: number) => onPlayback({ progress: value });
  const bars = useMemo(() => voiceBars(waveform), [waveform]);

  useEffect(() => {

    const audio = audioRef.current;

    return () => {

      if (audio && currentVoice === audio) currentVoice = null;

      audio?.pause();

    };

  }, [src]);

  const toggle = () => {

    const audio = audioRef.current;

    if (!audio) return;

    if (audio.paused) {

      if (currentVoice && currentVoice !== audio) currentVoice.pause();

      currentVoice = audio;
      audio.play().catch(() => setPlaying(false));
      return;

    }

    audio.pause();

  };

  const seekTo = (clientX: number) => {

    const audio = audioRef.current;
    const track = waveformRef.current;

    if (!audio || !track || !src) return;

    const { left, width } = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - left) / width));
    const duration = Number.isFinite(audio.duration) && audio.duration > 0
      ? audio.duration
      : (durationMs ?? 0) / 1000;

    if (!duration) return;

    audio.currentTime = ratio * duration;
    setProgress(ratio);

  };

  return (
    <div className={styles.player}>
      <div className={styles.playColumn}>
        <button
          type="button"
          className={cx(styles.play, playing && styles.playActive)}
          onClick={toggle}
          disabled={!src}
          aria-label={t(playing ? 'Alarm_Detail_Chat_Pause' : 'Alarm_Detail_Chat_Play')}
        >
          <Icon path={playing ? mdiPause : mdiPlay} size="22px"/>
        </button>
        {typeof durationMs === 'number' && (
          <span className={styles.duration}>{formatDuration(durationMs)}</span>
        )}
      </div>
      <div
        ref={waveformRef}
        className={cx(styles.waveform, src && styles.waveformScrub)}
        tabIndex={src ? 0 : undefined}
        aria-label={t('Alarm_Detail_Chat_RecordingPosition')}
        onKeyDown={(event) => {

          const audio = audioRef.current;

          if (!audio || !src) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

          event.preventDefault();

          const duration = Number.isFinite(audio.duration) && audio.duration > 0
            ? audio.duration
            : (durationMs ?? 0) / 1000;

          if (!duration) return;

          const next = Math.min(duration, Math.max(0, audio.currentTime + (event.key === 'ArrowRight' ? 5 : -5)));

          audio.currentTime = next;
          setProgress(next / duration);

        }}
        onPointerDown={(event) => {

          if (!src) return;

          event.currentTarget.setPointerCapture(event.pointerId);
          seekTo(event.clientX);

        }}
        onPointerMove={(event) => {

          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;

          seekTo(event.clientX);

        }}
      >
        {bars.map((value, index) => (
          <span
            key={index}
            className={index / bars.length < progress ? styles.played : undefined}
            style={{ height: `${Math.round(4 + value * 24)}px` }}
          />
        ))}
      </div>
      {src && (
        <audio
          ref={audioRef}
          className={styles.audio}
          src={src}
          preload="metadata"
          onLoadedMetadata={(event) => {

            const { duration } = event.currentTarget;

            if (Number.isFinite(duration) && duration > 0) onPlayback({ audioDurationMs: duration * 1000 });

          }}
          onPlay={() => setPlaying(true)}
          onPause={() => {

            setPlaying(false);

            if (currentVoice === audioRef.current) currentVoice = null;

          }}
          onEnded={() => {

            setPlaying(false);
            setProgress(0);

          }}
          onTimeUpdate={(event) => {

            const audio = event.currentTarget;

            if (!audio.duration) return;

            setProgress(audio.currentTime / audio.duration);

          }}
        />
      )}
    </div>
  );

};

const translationFor = (
  translations: Record<string, string> | null | undefined,
  locales: string[],
): string | undefined => {

  if (!translations) return undefined;

  const entries = Object.entries(translations);

  return locales.reduce<string | undefined>((found, locale) => {

    if (found) return found;

    const exact = translations[locale]?.trim();

    if (exact) return exact;

    const prefix = locale.slice(0, 2).toLowerCase();
    const match = entries.find(([key]) => {

      const normalized = key.toLowerCase();

      return normalized === prefix || normalized.startsWith(`${prefix}-`);

    });

    return match?.[1]?.trim() || undefined;

  }, undefined);

};

const participantName = (event: AlarmCallEvent, participants: AlarmCallParticipant[]): string => {

  const participant = participants.find((item) => item._id === event.participant);

  return participant?.name ?? '…';

};

const Bubble: FC<{
  name: string;
  at: number;
  mine?: boolean;
  children: React.ReactNode;
}> = ({
  name, at, mine, children,
}) => (
  <div className={cx(messageStyles.container, mine ? messageStyles.right : messageStyles.left)}>
    <div className={messageStyles.mainLayout}>
      <div className={messageStyles.mainColumn}>
        <div className={messageStyles.titleRow}>
          <div className={messageStyles.title}>{name}</div>
        </div>
        <div
          className={cx(messageStyles.bubble, styles.item, mine ? styles.itemMine : styles.itemTheirs)}
        >
          <div className={messageStyles.content}>
            <div className={messageStyles.contentTextWrapper}>
              <div className={messageStyles.contentText}>{children}</div>
            </div>
          </div>
          <div
            className={messageStyles.time}
            style={{ color: mine ? colors.textMedium : colors.textSemiTransparent }}
          >
            {moment(at).format('HH:mm')}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const dayStartMs = (at: number): number => {

  const date = new Date(at);

  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

};

const DateSeparator: FC<{ day: number; locale: string }> = ({ day, locale }) => (

  <div className={styles.dateSeparator}>
    <span/>
    <time>{new Date(day).toLocaleDateString(locale, { dateStyle: 'long' })}</time>
    <span/>
  </div>
);

const withDateSeparators = (rows: Row[], locale: string): Row[] => {

  if (rows.length === 0) return rows;

  const today = dayStartMs(Date.now());
  const dated: Row[] = [];
  let previousDay: number | undefined;

  rows.forEach((row) => {

    const day = dayStartMs(row.at);

    if (!Number.isFinite(day)) {

      dated.push(row);
      return;

    }

    if (day !== previousDay) {

      dated.push({
        id: `date-${day}`,
        at: day,
        node: <DateSeparator day={day} locale={locale}/>,
      });

    }

    dated.push(row);
    previousDay = day;

  });

  if (previousDay !== undefined && previousDay !== today) {

    dated.push({
      id: `date-${today}`,
      at: today,
      node: <DateSeparator day={today} locale={locale}/>,
    });

  }

  return dated;

};

const Notice: FC<{ text: string; at?: number }> = ({ text, at }) => (

  <div className={styles.notice}>
    <span>{text}</span>
    {typeof at === 'number' && Number.isFinite(at) && (
      <time>{moment(at).format('HH:mm')}</time>
    )}
  </div>
);

const isFresh = (lastHeartbeatAt: string, now: number): boolean => {

  const elapsed = now - Date.parse(lastHeartbeatAt);

  return elapsed < heartbeatMs;

};

const activeSegmentIndex = (segments: AlarmCallEventAudioTranscriptSegment[], seconds: number): number => segments
  .reduce((active, { start }, index) => (seconds >= start ? index : active), 0);

const TranscriptKaraoke: FC<{
  segments: AlarmCallEventAudioTranscriptSegment[];
  seconds: number;
}> = ({ segments, seconds }) => {

  const active = activeSegmentIndex(segments, seconds);

  return (
    <p>
      {segments.map((segment, index) => (
        <span
          key={`${segment.start}-${index}`}
          className={cx(styles.segment, index === active && styles.segmentActive)}
        >
          {`${segment.text.trim()}${index < segments.length - 1 ? ' ' : ''}`}
        </span>
      ))}
    </p>
  );

};

const Transcript: FC<{
  event: AlarmCallEvent;
  playback: Playback;
  durationMs?: number;
}> = ({ event, playback: { playing, progress, audioDurationMs }, durationMs }) => {

  const { t, iso, bcp } = useLocale();
  const [showOriginal, setShowOriginal] = useState(false);

  if (event.content.type !== AlarmCallEventContentType.Audio) return null;
  if (event.content.state.phase !== AlarmCallEventAudioPhase.Processing
    && event.content.state.phase !== AlarmCallEventAudioPhase.Published) return null;

  const transcript = event.content.state.metadata?.transcript;

  if (!transcript) return null;

  if (transcript.state === AlarmCallEventTranscriptState.Loading) {

    return <p className={styles.transcript}>{t('Alarm_Detail_Chat_Transcribing')}</p>;

  }

  if (transcript.state !== AlarmCallEventTranscriptState.Success) return null;

  const translated = translationFor(transcript.translations, [bcp, iso, localeCode(iso)]);
  const sourceLocale = transcript.locale?.slice(0, 2).toLowerCase();
  const needsTranslation = !!translated && sourceLocale !== localeCode(iso).toLowerCase();
  const display = needsTranslation && !showOriginal ? translated : transcript.text;
  const { segments } = transcript;
  // Same as the app: only the spoken text is highlighted (segments are its timestamps), also while paused mid-message.
  const karaoke = !!segments?.length && display === transcript.text && (playing || (progress > 0 && progress < 1));
  const seconds = progress * ((audioDurationMs ?? durationMs ?? 0) / 1000);

  return (
    <div className={styles.transcript}>
      {karaoke && segments ? <TranscriptKaraoke segments={segments} seconds={seconds}/> : <p>{display}</p>}
      <div className={styles.transcriptActions}>
        <span>{t('Alarm_Detail_Chat_TranscriptWarning')}</span>
        {needsTranslation && (
          <button
            type="button"
            className={cx(!showOriginal && styles.translationActive)}
            title={t(showOriginal ? 'Alarm_Detail_Chat_ShowTranslation' : 'Alarm_Detail_Chat_ShowOriginal')}
            aria-label={t(showOriginal ? 'Alarm_Detail_Chat_ShowTranslation' : 'Alarm_Detail_Chat_ShowOriginal')}
            aria-pressed={!showOriginal}
            onClick={() => setShowOriginal((value) => !value)}
          >
            <Icon path={mdiTranslate} size="14px"/>
          </button>
        )}
      </div>
    </div>
  );

};

const Voice: FC<{
  event: AlarmCallEvent;
  name: string;
}> = ({ event, name }) => {

  const { t } = useLocale();
  const [playback, setPlayback] = useState<Playback>({ playing: false, progress: 0 });
  const onPlayback = (change: Partial<Playback>) => setPlayback((current) => ({ ...current, ...change }));

  if (event.content.type !== AlarmCallEventContentType.Audio) return null;

  const { state } = event.content;

  if (state.phase === AlarmCallEventAudioPhase.Deleted) {

    return <div className={styles.notice}>{t('Alarm_Detail_Chat_Deleted')}</div>;

  }

  if (state.phase !== AlarmCallEventAudioPhase.Processing && state.phase !== AlarmCallEventAudioPhase.Published) {

    return null;

  }

  const at = state.phase === AlarmCallEventAudioPhase.Published
    ? Date.parse(state.publishedAt)
    : Date.parse(event.createdAt);

  return (
    <Bubble name={name} at={at}>
      <div className={styles.voice}>
        <VoicePlayer
          src={state.phase === AlarmCallEventAudioPhase.Published ? state.src ?? undefined : undefined}
          durationMs={state.metadata?.durationMs}
          waveform={state.metadata?.waveform ?? []}
          playback={playback}
          onPlayback={onPlayback}
        />
        <Transcript event={event} playback={playback} durationMs={state.metadata?.durationMs}/>
      </div>
    </Bubble>
  );

};

export const Timeline: FC<Props> = ({
  callEvents,
  participants,
  operatorMessages,
  optimisticOperator,
  logs,
}) => {

  const { t, bcp } = useLocale();
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const seenRowIds = useRef<Set<string> | null>(null);
  const pendingOwnText = useRef<string[]>([]);
  const [freshIds, setFreshIds] = useState<ReadonlySet<string>>(() => new Set());
  const [now, setNow] = useState(() => Date.now());

  const hasLive = callEvents.some((event) => (
    (event.content.type === AlarmCallEventContentType.Chat && event.content.state.phase === AlarmCallEventChatPhase.Started)
    || (event.content.type === AlarmCallEventContentType.Audio && event.content.state.phase === AlarmCallEventAudioPhase.Started)
  ));

  useEffect(() => {

    if (!hasLive) return undefined;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);

  }, [hasLive]);

  const rows = useMemo(() => {

    const operatorNames = operatorDisplayNames(
      [...operatorMessages, ...logs],
      t('Alarm_Detail_Logs_Operator'),
      (number) => t('Alarm_Detail_Logs_OperatorNumber', { number }),
    );
    const next: Row[] = [];

    callEvents.forEach((event) => {

      const at = Date.parse(event.createdAt);
      const name = participantName(event, participants);

      if (event.content.type === AlarmCallEventContentType.Chat) {

        if (event.content.state.phase === AlarmCallEventChatPhase.Canceled) return;

        if (event.content.state.phase === AlarmCallEventChatPhase.Started) {

          if (!isFresh(event.content.lastHeartbeatAt, now)) return;

          next.push({
            id: event._id,
            at,
            node: <div className={styles.notice}>{t('Alarm_Detail_Chat_Typing', { name })}</div>,
          });
          return;

        }

        next.push({
          id: event._id,
          at: Date.parse(event.content.state.publishedAt),
          node: (
            <Bubble name={name} at={Date.parse(event.content.state.publishedAt)}>
              {event.content.state.text}
            </Bubble>
          ),
        });
        return;

      }

      if (event.content.type === AlarmCallEventContentType.Audio) {

        if (event.content.state.phase === AlarmCallEventAudioPhase.Canceled) return;

        if (event.content.state.phase === AlarmCallEventAudioPhase.Started) {

          if (!isFresh(event.content.lastHeartbeatAt, now)) return;

          next.push({
            id: event._id,
            at,
            node: <div className={styles.notice}>{t('Alarm_Detail_Chat_Speaking', { name })}</div>,
          });
          return;

        }

        next.push({
          id: event._id,
          at: event.content.state.phase === AlarmCallEventAudioPhase.Published
            ? Date.parse(event.content.state.publishedAt)
            : at,
          node: <Voice event={event} name={name}/>,
        });
        return;

      }

      next.push({
        id: event._id,
        at,
        node: <Notice text={t('Alarm_Detail_Chat_Position')} at={at}/>,
      });

    });

    operatorMessages.forEach((event) => {

      if (event.data.eventType !== AlarmLogEventKind.OperatorChat) return;

      next.push({
        id: event._id,
        at: Date.parse(event.createdAt),
        ownText: event.data.message,
        node: (
          <Bubble
            mine
            name={event.createdBy && 'name' in event.createdBy && event.createdBy.name
              ? event.createdBy.name
              : operatorNames.get(event.createdBy?._id ?? '') ?? t('Alarm_Detail_Logs_Operator')}
            at={Date.parse(event.createdAt)}
          >
            {event.data.message}
          </Bubble>
        ),
      });

    });

    optimisticOperator.forEach((event) => {

      next.push({
        id: event._id,
        at: Date.parse(event.createdAt),
        ownText: event.message,
        node: (
          <Bubble mine name={event.name} at={Date.parse(event.createdAt)}>
            {event.message}
          </Bubble>
        ),
      });

    });

    logs.forEach((event) => {

      if (event.data?.eventType !== AlarmLogEventKind.CallSession) return;
      if (event.data.callType !== AlarmCallType.HelperCall) return;
      if (event.data.phase !== AlarmLogCallSessionPhase.Join && event.data.phase !== AlarmLogCallSessionPhase.Leave) return;

      const name = event.createdBy && 'name' in event.createdBy ? event.createdBy.name : '';

      next.push({
        id: `boundary-${event._id}`,
        at: Date.parse(event.createdAt),
        node: (
          <Notice
            text={t(event.data.phase === AlarmLogCallSessionPhase.Join
              ? 'Alarm_Detail_Chat_Joined'
              : 'Alarm_Detail_Chat_Left', { name })}
            at={Date.parse(event.createdAt)}
          />
        ),
      });

    });

    return withDateSeparators(next.sort((a, b) => a.at - b.at), bcp);

  }, [bcp, callEvents, logs, now, operatorMessages, optimisticOperator, participants, t]);

  useEffect(() => {

    const ids = rows.map((row) => row.id);

    if (!seenRowIds.current) {

      seenRowIds.current = new Set(ids);
      return;

    }

    const arrived = ids.filter((id) => !seenRowIds.current?.has(id) && !id.startsWith('date-'));

    ids.forEach((id) => seenRowIds.current?.add(id));

    const ordered = [
      ...arrived.filter((id) => id.startsWith('local-')),
      ...arrived.filter((id) => !id.startsWith('local-')),
    ];

    const highlight = ordered.filter((id) => {

      const ownText = rows.find((row) => row.id === id)?.ownText;

      if (id.startsWith('local-')) {

        if (ownText) pendingOwnText.current.push(ownText);

        return false;

      }

      if (!ownText) return true;

      const echo = pendingOwnText.current.indexOf(ownText);

      if (echo < 0) return true;

      pendingOwnText.current.splice(echo, 1);

      return true;

    });

    if (highlight.length === 0) return;

    setFreshIds((current) => new Set([...current, ...highlight]));

  }, [rows]);

  useEffect(() => {

    const list = listRef.current;

    if (!list) return;

    if (stickToBottom.current) list.scrollTop = list.scrollHeight;

  }, [rows]);

  if (rows.length === 0) {

    return <p className={styles.empty}>{t('Alarm_Detail_Log_No_Messages')}</p>;

  }

  return (
    <div
      ref={listRef}
      className={styles.list}
      onScroll={(event) => {

        const list = event.currentTarget;
        stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < 120;

      }}
    >
      {rows.map((row) => (
        <div key={row.id} className={freshIds.has(row.id) ? styles.fresh : undefined}>{row.node}</div>
      ))}
    </div>
  );

};
